// `collaborator_portal_access` non ha una colonna discriminatore fra "Portale partner" e
// "Portale progetti": entrambe le liste (PartnerAuthService.getPartnerAccessList e
// CollaboratorAuthService.getCollaboratorAccessList) restituiscono TUTTE le righe dell'org,
// e le due sezioni della UI smistavano solo per `external_partner_id`. Se un partner ha
// entrambi gli accessi, le due liste contengono le stesse due righe e `.find()` può
// facilmente prendere quella sbagliata: da lì "Modifica" + Salva sulla sezione sbagliata
// sovrascrive `permissions` con la forma opposta (jsonb collaboratore sovrascritto con un
// array partner, o viceversa), e "Nuova password"/"Disattiva" agiscono sulla riga sbagliata.
//
// Fix minimo lato UI, senza toccare schema/servizi (in hardening su un'altra sessione in
// parallelo): si deduce il "tipo" della riga guardando quali chiavi di permesso sono
// effettivamente attive, e ogni sezione filtra sulla propria kind. Il fix vero — una colonna
// `portal_type` su `collaborator_portal_access` — resta un follow-up.

export const PARTNER_PERMISSION_KEYS = ['view_services', 'request_quotes', 'view_reports'] as const;

export const COLLABORATOR_PERMISSION_KEYS = [
  'view_assigned_tasks',
  'update_task_status',
  'add_comments',
  'view_public_comments',
  'upload_attachments',
  'log_time'
] as const;

export type PortalAccessKind = 'partner' | 'collaboratore';

/**
 * Determina il "tipo" di una riga `collaborator_portal_access` guardando le chiavi di
 * permesso attive in `access.permissions`, che può arrivare come ARRAY (le liste passate da
 * PartnerAuthService sono già normalizzate così) o come OGGETTO jsonb grezzo (come restituito
 * da CollaboratorAuthService.getCollaboratorAccessList, che non normalizza).
 */
export function portalAccessKind(access: { permissions: unknown }): PortalAccessKind {
  const activeKeys = normalizeActivePermissionKeys(access.permissions);

  if (activeKeys.some((k) => (PARTNER_PERMISSION_KEYS as readonly string[]).includes(k))) {
    return 'partner';
  }
  if (activeKeys.some((k) => (COLLABORATOR_PERMISSION_KEYS as readonly string[]).includes(k))) {
    return 'collaboratore';
  }

  // Ambiguo/vuoto: il default a DB della colonna `permissions` è l'oggetto collaboratore, e
  // un accesso partner porta sempre almeno un permesso di default (view_services +
  // request_quotes) a meno che non venga svuotato deliberatamente in modifica. In quel caso
  // limite scegliamo "collaboratore": è il default della colonna, ed è l'esito meno rischioso
  // (la sezione "Portale partner" scriverebbe un array al posto del jsonb collaboratore).
  return 'collaboratore';
}

function normalizeActivePermissionKeys(raw: unknown): string[] {
  let value = raw;

  if (typeof value === 'string') {
    try {
      value = JSON.parse(value);
    } catch {
      return [];
    }
  }

  if (Array.isArray(value)) {
    return value.filter((v): v is string => typeof v === 'string');
  }

  if (value && typeof value === 'object') {
    return Object.entries(value as Record<string, unknown>)
      .filter(([, enabled]) => enabled === true)
      .map(([key]) => key);
  }

  return [];
}
